import { Dayjs } from 'dayjs'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { DATE_FORMAT, PST_TZ } from '~constants'
import { ArtistProps, CalendarDayEventProps } from '../types'
import { EventsByDate, FormattedEvent } from '../types/Event'
import logger from 'utils/logger-aws'
import { DATE_FORMATTER, TM_CTA_TITLE_DEFAULT } from '../../../../constants/calendar'
import cx from 'classnames'
import { getEventCtaPrimaryOverride } from './getEventCtaPrimaryOverride'

export const getEventTimes = (events: FormattedEvent[], artistId: string) => {
  return events
    .filter(event => event.artistId == artistId)
    .map(item => dayjs(item.dateTime).format(DATE_FORMATTER.TIME))
}

export const getMarkedEventTimes = (events: FormattedEvent[], artistId: string) => {
  return events
    .filter(event => event.artistId == artistId)
    .map(item => {
      const formattedTime = dayjs(item.dateTime).format(DATE_FORMATTER.TIME)
      // If the event has sphereFlags, mark it with an asterisk
      if (item.sphereFlags && item.sphereFlags.length > 0) {
        return formattedTime + '*'
      }
      return formattedTime
    })
}

export const getEverySphereFlagDescription = (events: FormattedEvent[]) => {
  const sphereFlagsSet = new Set<string>()
  events.forEach(event => {
    if (event.sphereFlags && event.sphereFlags.length > 0) {
      event.sphereFlags.forEach(flag => sphereFlagsSet.add(flag.html_description))
    }
  })
  return Array.from(sphereFlagsSet)
}

export const generateCalendarStartAndEndDate = (startDate: DayjsInput) => {
  // Date should be valid format
  if (!startDate) {
    logger.error(' [generateCalendarStartAndEndDate] - Date is required to generate start and end date')
  }
  // Assuming date is a valid DayjsInput, we can generate start and end dates
  const currentStartDate = startDate
    ? dayjs(startDate as string)
        .startOf('month')
        .startOf('day')
    : dayjs.tz(PST_TZ).startOf('day')

  const calendarStartDate = currentStartDate.format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
  // Check if current date is in the last week of the month
  const isLastWeekOfMonth = currentStartDate.isSame(currentStartDate.clone().endOf('month'), 'week')
  if (isLastWeekOfMonth) {
    const endOfCurrentMonth = currentStartDate.clone().endOf('month')
    let nextMonthEndDate = endOfCurrentMonth
      .clone()
      .add(1, 'day')
      .endOf('week')
      .format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)

    const endOfMonthDay = endOfCurrentMonth.day()
    if (endOfMonthDay >= 0 && endOfMonthDay <= 2) {
      // Sunday, Monday, Tuesday
      nextMonthEndDate = endOfCurrentMonth
        .clone()
        .add(6 - endOfMonthDay, 'days')
        .format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    } else {
      // Wednesday, Thursday, Friday, Saturday
      nextMonthEndDate = endOfCurrentMonth
        .clone()
        .add(7 + (6 - endOfMonthDay), 'days')
        .format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    }
    return {
      startDate: calendarStartDate,
      endDate: nextMonthEndDate
    }
  } else {
    let endOfMonth = currentStartDate.clone().endOf('month')
    if (['Sunday', 'Monday', 'Tuesday'].includes(endOfMonth.format(DATE_FORMAT.DAY_NAME))) {
      endOfMonth = endOfMonth.endOf('week')
    } else {
      const endOfMonthDay = endOfMonth.day()
      // Extend to the end of that week to fill the calendar remaining days
      endOfMonth = endOfMonth.clone().add(6 - endOfMonthDay, 'days')
    }
    const calendarEndDate = endOfMonth.format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    return {
      startDate: calendarStartDate,
      endDate: calendarEndDate
    }
  }
}

export const collectArtistIds = (artists: ArtistProps[]) => {
  if (!artists?.length) return ''
  return artists.map(artist => artist?.artistId).join(',')
}

export const isNextButtonDisabled = (selectedMonth: string, activeMonthList: string[]) => {
  if (!selectedMonth || !activeMonthList?.length) return true
  const currentMonthIndex = activeMonthList.indexOf(selectedMonth)
  return currentMonthIndex === activeMonthList.length - 1
}

export const isPrevButtonDisabled = (selectedMonth: string, activeMonthList: string[]) => {
  if (!selectedMonth || !activeMonthList?.length) return true
  const currentMonthIndex = activeMonthList.indexOf(selectedMonth)
  return currentMonthIndex === 0
}

//Generate visible calendar days for selected month
export const getCalendarDays = (
  cmStartDate: Dayjs,
  startDate: Dayjs,
  endDate: Dayjs,
  eventsByDate: EventsByDate | null
): CalendarDayEventProps[][] => {
  const today = dayjs()
  const calendarDays: CalendarDayEventProps[][] = []
  let week: CalendarDayEventProps[] = []

  if (!eventsByDate) return calendarDays

  for (let date = startDate.clone(); date.isSameOrBefore(endDate, 'day'); date = date.add(1, 'day')) {
    const dateKey = date.format(DATE_FORMAT.SHORT_DATE_FORMAT)
    week.push({
      date,
      events: eventsByDate[dateKey] ?? [],
      isFromBeforeCurrentMonth: date.isBefore(cmStartDate, 'day'),
      isCurrentDate: date.isSame(today, 'day'),
      isPastDate: date.isBefore(today, 'day')
    })

    if (week.length === 7) {
      calendarDays.push(week)
      week = []
    }
  }

  if (week.length > 0) {
    calendarDays.push(week)
  }

  return calendarDays
}

export const getCalendarDayClasses = (eventInfo: CalendarDayEventProps) => {
  if (!eventInfo?.events) {
    return cx({ calendar__day: true })
  }
  const today = dayjs()
  const eventDate = eventInfo.date
  const isPastDate = eventInfo.isPastDate
  const dayHasEvents = eventInfo.events.length > 0
  const isToday = eventDate.isSame(new Date(), 'day')
  const isBeforeToday = eventDate.isBefore(today, 'day')
  const classes = {
    calendar__day: true,
    'calendar__day--is-today': isToday,
    'calendar__day--has-passed': isBeforeToday || (isPastDate && !dayHasEvents && !isToday),
    'calendar__day--is-from-different-month': eventInfo?.isFromBeforeCurrentMonth
  }

  return cx(classes)
}

type Variant = 'desktop' | 'tablet'

export const getStartDate = (month: Dayjs, variant: Variant, today: Dayjs): Dayjs => {
  const firstDayOfMonth = month.startOf('month')
  return variant === 'tablet' && firstDayOfMonth.isBefore(today) ? today.clone() : firstDayOfMonth
}

export const getCalendarEndDate = (endDate: Dayjs): Dayjs => {
  const dayOfWeek = endDate.day()
  // Extend to the end of that week to fill the calendar remaining days
  return dayOfWeek <= 2 ? endDate.endOf('week') : endDate.add(6 - dayOfWeek, 'days')
}

export const getUniqueArtists = (eventInfo: CalendarDayEventProps): FormattedEvent[] => {
  return eventInfo.events.filter((value, index, self) => index === self.findIndex(t => t.artistId === value.artistId))
}

type CtaData = {
  url: string | null
  title: string | null
}

export const getCtaData = (
  eventInfo: FormattedEvent | null,
  artist: ArtistProps | null | undefined,
  shouldAllowSoldOut: boolean = false
): CtaData => {
  if (!eventInfo) return { title: TM_CTA_TITLE_DEFAULT.onsale, url: null }
  const { hostUrl, isSoldOut } = eventInfo || {}
  const ctaOverrides = artist ? getEventCtaPrimaryOverride(artist) : null

  let url: string | null = null

  if (ctaOverrides?.url) {
    url = ctaOverrides.url
  } else if (hostUrl) {
    const parsedUrl = new URL(hostUrl)
    parsedUrl.searchParams.append('utm_source', 'sphere')
    parsedUrl.searchParams.append('utm_medium', 'web')
    parsedUrl.searchParams.append('camefrom', 'CFC_SPHERE')
    url = parsedUrl.toString()
  }

  let title: string | null
  if (shouldAllowSoldOut && isSoldOut) {
    title = TM_CTA_TITLE_DEFAULT.sold_out
  } else if (ctaOverrides?.title) {
    title = ctaOverrides.title
  } else {
    title = TM_CTA_TITLE_DEFAULT.onsale
  }

  return { url, title }
}

export const filterPastEvents = (events: FormattedEvent[]) => {
  const now = dayjs().tz(PST_TZ)
  return events.filter(e => {
    const eventDate = dayjs(e.dateTime).tz(PST_TZ)
    if (eventDate.isSame(now, 'day')) {
      // Remove events that started more than 1 hour ago (event time + 1hr <= now)
      return eventDate.add(1, 'hour').isAfter(now)
    }
    return true
  })
}
